import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  CountrySelect,
  CLIENT_LOCALE,
  cn,
  opportunityCityForEdit,
  opportunityCountryForEdit,
  structuredLocationPayload,
  validateOpportunityLocationFields,
  FeaturedJobFormHelp,
  GatePeekPanel,
  FEATURED_JOB_FEATURE_KEY,
  gatingSurfaceTitle,
  UpgradeNotice,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useGatedAction,
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  navigateWithThreadPreservation,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import type { EntitlementDeniedPayload } from '@yeshuman/sdk'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  EMPLOYMENT_TYPE_LABELS,
  SALARY_PERIOD_LABELS,
  WORK_MODE_LABELS,
  normalizeSalaryPeriodKey,
  normalizeSelectEnumKey,
} from '../utils/opportunity-job-meta'
import { LockKeyhole } from 'lucide-react'


import {
  type OrganisationOpportunity,
  type OpportunityMutationError,
  useOrganisationOpportunities,
  useOpportunityMutations,
} from '../hooks/use-organisation-opportunities'
import { JOB_DETAILS_CANVAS_FIELDS, opportunityCanvasSurface } from '../lib/canvas-ui-surface'


export interface OpportunityDetailsDraft {
  title: string
  employment_type: string
  work_mode: string
  city: string
  country_code: string
  salary_visibility: 'shown' | 'not_disclosed'
  salary_min: string
  salary_max: string
  salary_currency: string
  salary_period: string
  /** Employer intent to feature this job when published. */
  is_featured: boolean
}

function RequiredMark() {
  return (
    <>
      <span className="text-destructive" aria-hidden="true">
        *
      </span>
      <span className="sr-only"> (required)</span>
    </>
  )
}

function buildDraft(opp: OrganisationOpportunity): OpportunityDetailsDraft {
  return {
    title: opp.title,
    employment_type: normalizeSelectEnumKey(opp.employment_type, EMPLOYMENT_TYPE_LABELS),
    work_mode: normalizeSelectEnumKey(opp.work_mode, WORK_MODE_LABELS),
    city: opportunityCityForEdit(opp.city, opp.location),
    country_code: opportunityCountryForEdit(opp.country_code, CLIENT_LOCALE),
    salary_visibility: (opp.salary_visibility === 'shown' ? 'shown' : 'not_disclosed'),
    salary_min:
      opp.salary_min != null && !Number.isNaN(Number(opp.salary_min))
        ? String(opp.salary_min)
        : '',
    salary_max:
      opp.salary_max != null && !Number.isNaN(Number(opp.salary_max))
        ? String(opp.salary_max)
        : '',
    salary_currency: opp.salary_currency?.trim().toUpperCase() || '',
    salary_period: normalizeSalaryPeriodKey(opp.salary_period),
    is_featured: Boolean(opp.is_featured),
  }
}

interface OpportunityDetailsCanvasProps {
  organisationSlug: string
  /** Slug-free workspace updates when set (edit mode on job workspace). */
  opportunityId?: string
  opportunity: OrganisationOpportunity
  onClose: () => void
  onSaved?: (updated: OrganisationOpportunity) => void
}

export function OpportunityDetailsCanvas({
  organisationSlug,
  opportunityId,
  opportunity,
  onClose,
  onSaved,
}: OpportunityDetailsCanvasProps) {
  const { setCanvasSurface } = useArtifactCanvas()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgMutations = useOrganisationOpportunities(organisationSlug)
  const workspaceMutations = useOpportunityMutations(opportunityId ?? opportunity.id)
  const featuredGate = useGatedAction(organisationSlug, 'job_board.feature')
  const useSlugFreeUpdate = Boolean(opportunityId)
  const isUpdating = useSlugFreeUpdate ? workspaceMutations.isUpdating : orgMutations.isUpdating
  const updateError = useSlugFreeUpdate ? workspaceMutations.updateError : orgMutations.updateError

  const runUpdate = (
    data: Parameters<typeof orgMutations.updateOpportunity>[0]['data'],
    handlers: {
      onSuccess: (updated: OrganisationOpportunity) => void
      onError: (err: Error) => void
    },
  ) => {
    if (useSlugFreeUpdate) {
      void workspaceMutations
        .updateOpportunity(data)
        .then(handlers.onSuccess)
        .catch((err) => handlers.onError(err instanceof Error ? err : new Error(String(err))))
      return
    }
    void orgMutations
      .updateOpportunity({ id: opportunity.id, data })
      .then(handlers.onSuccess)
      .catch((err) => handlers.onError(err instanceof Error ? err : new Error(String(err))))
  }

  const [draft, setDraft] = useState<OpportunityDetailsDraft>(() =>
    buildDraft(opportunity),
  )

  /** Keep draft in sync with `opportunity` whenever the query delivers a new object (same id, new reference). */
  useLayoutEffect(() => {
    setDraft(buildDraft(opportunity))
  }, [opportunity])

  const baselineDraft = useMemo(() => buildDraft(opportunity), [opportunity])

  const [validationError, setValidationError] = useState<string | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [mutationDenial, setMutationDenial] = useState<EntitlementDeniedPayload | null>(null)
  const [featuredGateDialogOpen, setFeaturedGateDialogOpen] = useState(false)
  const surfaceId = opportunityId ?? opportunity.id

  useEffect(() => {
    setCanvasSurface(opportunityCanvasSurface(surfaceId, [...JOB_DETAILS_CANVAS_FIELDS]))
  }, [setCanvasSurface, surfaceId])

  useEffect(() => {
    setValidationError(null)
    setMutationError(null)
    setMutationDenial(null)
  }, [opportunity?.id])

  const handleMutationError = (err: unknown) => {
    const e = err as OpportunityMutationError
    if (e.denial) {
      setMutationDenial(e.denial)
      setMutationError(null)
      return
    }
    setMutationError(e instanceof Error ? e.message : 'Could not save job')
    setMutationDenial(null)
  }

  const isDirty =
    draft.title !== baselineDraft.title ||
    draft.employment_type !== baselineDraft.employment_type ||
    draft.work_mode !== baselineDraft.work_mode ||
    draft.city !== baselineDraft.city ||
    draft.country_code !== baselineDraft.country_code ||
    draft.salary_visibility !== baselineDraft.salary_visibility ||
    draft.salary_min !== baselineDraft.salary_min ||
    draft.salary_max !== baselineDraft.salary_max ||
    draft.salary_currency !== baselineDraft.salary_currency ||
    draft.salary_period !== baselineDraft.salary_period ||
    draft.is_featured !== baselineDraft.is_featured

  /** Salary is optional; when both amounts are present, the range is shown to candidates. */
  const resolveSalaryForSubmit = ():
    | { ok: true; salary_visibility: 'shown'; salary_min: number; salary_max: number; salary_currency: string; salary_period: string }
    | { ok: true; salary_visibility: 'not_disclosed'; salary_min: null; salary_max: null; salary_currency: string; salary_period: string }
    | { ok: false; message: string } => {
    const minT = draft.salary_min.replace(/\s/g, '')
    const maxT = draft.salary_max.replace(/\s/g, '')
    const cur = draft.salary_currency.trim().toUpperCase()
    const period = draft.salary_period.trim()

    const hasMin = minT.length > 0
    const hasMax = maxT.length > 0
    const wantsRange = hasMin || hasMax

    if (!wantsRange) {
      return {
        ok: true,
        salary_visibility: 'not_disclosed',
        salary_min: null,
        salary_max: null,
        salary_currency: '',
        salary_period: '',
      }
    }

    if (wantsRange && (!hasMin || !hasMax)) {
      return {
        ok: false,
        message:
          'Complete the full salary range, or clear both salary amounts.',
      }
    }

    const mn = Number.parseInt(minT, 10)
    const mx = Number.parseInt(maxT, 10)
    if (Number.isNaN(mn) || Number.isNaN(mx)) {
      return {
        ok: false,
        message: 'Enter min and max as whole numbers.',
      }
    }
    if (mn < 0 || mx < 0 || mn > mx) {
      return {
        ok: false,
        message: 'Salary minimum and maximum must be valid whole numbers, and maximum must be at least the minimum.',
      }
    }
    if (cur.length !== 3) {
      return {
        ok: false,
        message: 'Enter a 3-letter salary currency (e.g. AUD).',
      }
    }
    if (!period || !(period in SALARY_PERIOD_LABELS)) {
      return {
        ok: false,
        message: 'Choose a salary period.',
      }
    }
    return {
      ok: true,
      salary_visibility: 'shown',
      salary_min: mn,
      salary_max: mx,
      salary_currency: cur,
      salary_period: period,
    }
  }

  const resolveLocationForSubmit = ():
    | { ok: true; work_mode: string; city: string; country_code: string }
    | { ok: false; message: string } => {
    const location = validateOpportunityLocationFields({
      work_mode: draft.work_mode,
      city: draft.city,
      country_code: draft.country_code,
    })
    if (!location.ok) {
      return location
    }
    return { ok: true, ...structuredLocationPayload(draft) }
  }

  const showCityField =
    draft.work_mode === 'onsite' || draft.work_mode === 'hybrid'

  const handleSave = () => {
    if (!draft.title.trim()) {
      setValidationError('Job title is required')
      return
    }
    const sal = resolveSalaryForSubmit()
    if (!sal.ok) {
      setValidationError(sal.message)
      return
    }
    const loc = resolveLocationForSubmit()
    if (!loc.ok) {
      setValidationError(loc.message)
      return
    }
    const base = {
      title: draft.title.trim(),
      description: (opportunity.description ?? '').trim(),
      employment_type: draft.employment_type.trim(),
      work_mode: loc.work_mode,
      city: loc.city,
      country_code: loc.country_code,
      salary_visibility: sal.salary_visibility,
      salary_min: sal.salary_min,
      salary_max: sal.salary_max,
      salary_currency: sal.salary_currency,
      salary_period: sal.salary_period,
      is_featured: draft.is_featured,
    }
    setValidationError(null)
    setMutationError(null)
    setMutationDenial(null)
    runUpdate(
      {
        ...base,
      },
      {
        onSuccess: (updated) => {
          onSaved?.(updated)
          onClose()
        },
        onError: handleMutationError,
      },
    )
  }

  const combinedRemoteError =
    mutationError ??
    (updateError instanceof Error ? updateError.message : updateError ? String(updateError) : null)
  const isDraftLike = opportunity.publication_status === 'draft'
  const featuredQuotaExhausted =
    !isDraftLike &&
    featuredGate.plansEnabled &&
    !featuredGate.isLoading &&
    (featuredGate.quota?.exhausted ?? false)
  const featuredCheckboxDisabled = featuredQuotaExhausted && !draft.is_featured

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(e) => e.preventDefault()}
      >
        <section className="space-y-6" aria-label="Job details">
        <div className="space-y-2">
          <Label htmlFor="artifact-opp-title">
            Job title <RequiredMark />
          </Label>
          <Input
            id="artifact-opp-title"
            value={draft.title}
            onChange={(e) => {
              setDraft((d) => ({ ...d, title: e.target.value }))
              setValidationError(null)
            }}
            placeholder="e.g., Senior product manager"
            className="placeholder:italic"
            required
            aria-required="true"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-opp-employment">Employment type</Label>
            <Select
              value={draft.employment_type || undefined}
              onValueChange={(v) => setDraft((d) => ({ ...d, employment_type: v }))}
            >
              <SelectTrigger id="artifact-opp-employment">
                <SelectValue placeholder="Employment type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="artifact-opp-workmode">Work mode</Label>
            <Select
              value={draft.work_mode || undefined}
              onValueChange={(v) => setDraft((d) => ({ ...d, work_mode: v }))}
            >
              <SelectTrigger id="artifact-opp-workmode">
                <SelectValue placeholder="Work mode" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(WORK_MODE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-opp-country">Country</Label>
            <CountrySelect
              id="artifact-opp-country"
              value={draft.country_code}
              onValueChange={(code) => {
                setDraft((d) => ({ ...d, country_code: code }))
                setValidationError(null)
              }}
            />
          </div>
          {showCityField ? (
            <div className="space-y-2">
              <Label htmlFor="artifact-opp-city">City</Label>
              <Input
                id="artifact-opp-city"
                value={draft.city}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, city: e.target.value }))
                  setValidationError(null)
                }}
                placeholder="e.g., Sydney"
                className="placeholder:italic"
                maxLength={128}
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Base salary</Label>
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="artifact-opp-cur">Currency</Label>
                <Input
                  id="artifact-opp-cur"
                  value={draft.salary_currency}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      salary_currency: e.target.value.toUpperCase().slice(0, 3),
                    }))
                  }
                  placeholder="AUD"
                  maxLength={3}
                  className="placeholder:italic"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="artifact-opp-period">Period</Label>
                <Select
                  value={draft.salary_period || undefined}
                  onValueChange={(v) => setDraft((d) => ({ ...d, salary_period: v }))}
                >
                  <SelectTrigger id="artifact-opp-period">
                    <SelectValue placeholder="Pay period" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(SALARY_PERIOD_LABELS).map((p) => (
                      <SelectItem key={p} value={p}>
                        {p.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="artifact-opp-smin">Minimum</Label>
                <Input
                  id="artifact-opp-smin"
                  inputMode="numeric"
                  value={draft.salary_min}
                  onChange={(e) => setDraft((d) => ({ ...d, salary_min: e.target.value }))}
                  placeholder="90000"
                  className="placeholder:italic"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="artifact-opp-smax">Maximum</Label>
                <Input
                  id="artifact-opp-smax"
                  inputMode="numeric"
                  value={draft.salary_max}
                  onChange={(e) => setDraft((d) => ({ ...d, salary_max: e.target.value }))}
                  placeholder="120000"
                  className="placeholder:italic"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Leave both amounts blank to omit salary.
            </p>
          </div>
        </div>

        <div
          className={cn(
            'rounded-md border p-4 transition-colors',
            featuredCheckboxDisabled
              ? 'cursor-pointer border-promotion/30 bg-promotion/10'
              : 'border-border bg-muted/30',
          )}
          role={featuredCheckboxDisabled ? 'button' : undefined}
          tabIndex={featuredCheckboxDisabled ? 0 : undefined}
          aria-disabled={featuredCheckboxDisabled ? true : undefined}
          onClick={featuredCheckboxDisabled ? () => setFeaturedGateDialogOpen(true) : undefined}
          onKeyDown={
            featuredCheckboxDisabled
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setFeaturedGateDialogOpen(true)
                  }
                }
              : undefined
          }
        >
          <div className="flex items-start gap-3">
            <Checkbox
              id="artifact-opp-featured"
              checked={draft.is_featured}
              disabled={featuredCheckboxDisabled}
              onCheckedChange={(v) => {
                const enabling = v === true
                if (enabling && featuredQuotaExhausted) {
                  setFeaturedGateDialogOpen(true)
                  return
                }
                setDraft((d) => ({ ...d, is_featured: enabling }))
              }}
              className="mt-0.5"
              aria-describedby="artifact-opp-featured-help"
            />
            <div className="space-y-1">
              <Label
                htmlFor="artifact-opp-featured"
                className="cursor-pointer font-medium leading-snug"
              >
                Featured job
                {featuredCheckboxDisabled ? (
                  <LockKeyhole className="ml-2 inline h-3.5 w-3.5 text-promotion" aria-hidden />
                ) : null}
              </Label>
              <FeaturedJobFormHelp
                id="artifact-opp-featured-help"
                featuredActiveUntil={
                  opportunity?.featured_is_active ? opportunity.featured_until : null
                }
              />
            </div>
          </div>
        </div>
        </section>
        {(validationError || combinedRemoteError || mutationDenial) && (
          <div className="space-y-2">
            {validationError || (combinedRemoteError && !mutationDenial) ? (
              <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <p>{validationError ?? combinedRemoteError}</p>
              </div>
            ) : null}
            {mutationDenial ? (
              <UpgradeNotice
                denial={mutationDenial}
                planName={featuredGate.planName}
                featureLabel={featuredGate.featureLabel}
                quota={featuredGate.quota}
              />
            ) : null}
          </div>
        )}
      </form>

      <Dialog open={featuredGateDialogOpen} onOpenChange={setFeaturedGateDialogOpen}>
        <DialogContent className="sm:max-w-md border-promotion/30">
          <DialogHeader>
            <DialogTitle>
              {gatingSurfaceTitle(FEATURED_JOB_FEATURE_KEY, featuredGate.featureLabel)}
            </DialogTitle>
          </DialogHeader>
          <GatePeekPanel
            featureKey={FEATURED_JOB_FEATURE_KEY}
            featureLabel={featuredGate.featureLabel}
            planName={featuredGate.planName}
            upgradePath={featuredGate.upgradePath}
            quota={featuredGate.quota}
            onUpgrade={() => {
              setFeaturedGateDialogOpen(false)
              navigateWithThreadPreservation(navigate, featuredGate.upgradePath, searchParams)
            }}
          />
        </DialogContent>
      </Dialog>

      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isUpdating}
            saveLabel="Save"
            savingLabel="Saving…"
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
